<template>
  <div class="flex h-screen bg-gray-50">
    <!-- Sidebar -->
    <div class="w-64 bg-white border-l border-gray-200 p-6">
      <h2 class="text-xl font-bold text-gray-800 mb-6">المكاتب القانونية</h2>
      
      <!-- Search -->
      <div class="mb-6">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="بحث في المكاتب..."
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <!-- Filters -->
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">حجم المكتب</label>
          <select
            v-model="selectedSize"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">جميع الأحجام</option>
            <option value="small">صغير (1-5 محامين)</option>
            <option value="medium">متوسط (6-20 محامي)</option>
            <option value="large">كبير (21-50 محامي)</option>
            <option value="enterprise">مؤسسة (50+ محامي)</option>
          </select>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">المدينة</label>
          <select
            v-model="selectedCity"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">جميع المدن</option>
            <option v-for="city in cities" :key="city.id" :value="city.id">
              {{ city.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">التخصصات</label>
          <select
            v-model="selectedSpecialization"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">جميع التخصصات</option>
            <option v-for="spec in specializations" :key="spec.id" :value="spec.id">
              {{ spec.name }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 p-6">
      <div class="max-w-6xl mx-auto">
        <!-- Firms Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="firm in filteredFirms"
            :key="firm.id"
            class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
            @click="openFirmModal(firm)"
          >
            <div class="flex items-start space-x-4 space-x-reverse mb-4">
              <img
                :src="firm.logo"
                :alt="firm.name"
                class="w-16 h-16 rounded-lg object-cover"
              />
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-800 mb-1">{{ firm.name }}</h3>
                <p class="text-sm text-gray-600">{{ firm.specialization }}</p>
              </div>
            </div>
            
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center">
                <div class="flex text-yellow-400">
                  <Star
                    v-for="i in firm.rating"
                    :key="i"
                    :class="i < firm.rating ? 'w-4 h-4 fill-current' : 'w-4 h-4'"
                  />
                </div>
                <span class="text-sm text-gray-600 mr-1">({{ firm.rating }})</span>
              </div>
            </div>

            <div class="flex items-center text-xs text-gray-500 mb-3">
              <MapPin class="w-3 h-3 ml-1" />
              <span>{{ firm.city }}</span>
              <span class="mx-2">•</span>
              <span>{{ firm.lawyersCount }} محامي</span>
              <span class="mx-2">•</span>
              <span>{{ firm.foundedYear }}</span>
            </div>

            <div class="flex items-center justify-between">
              <span class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                {{ firm.casesHandled }} قضية
              </span>
              <button class="text-blue-600 hover:text-blue-800 text-sm font-medium">
                عرض التفاصيل
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredFirms.length === 0" class="text-center py-12">
          <Building class="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p class="text-gray-600">لم يتم العثور على مكاتب مطابقة للبحث</p>
        </div>
      </div>
    </div>

    <!-- Firm Details Modal -->
    <div
      v-if="selectedFirm"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click="closeFirmModal"
    >
      <div
        class="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        @click.stop
      >
        <div class="p-6 border-b border-gray-200">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-2xl font-bold text-gray-800">تفاصيل المكتب القانوني</h2>
            <button
              @click="closeFirmModal"
              class="text-gray-400 hover:text-gray-600"
            >
              <X class="w-6 h-6" />
            </button>
          </div>
          
          <div class="flex items-start space-x-6 space-x-reverse">
            <img
              :src="selectedFirm.logo"
              :alt="selectedFirm.name"
              class="w-24 h-24 rounded-lg object-cover"
            />
            <div class="flex-1">
              <h1 class="text-2xl font-bold text-gray-800 mb-2">{{ selectedFirm.name }}</h1>
              <p class="text-lg text-gray-600 mb-3">{{ selectedFirm.specialization }}</p>
              
              <div class="grid grid-cols-2 gap-4 mb-4">
                <div class="flex items-center">
                  <MapPin class="w-5 h-5 text-gray-400 ml-2" />
                  <span class="text-gray-600">{{ selectedFirm.city }}</span>
                </div>
                <div class="flex items-center">
                  <Users class="w-5 h-5 text-gray-400 ml-2" />
                  <span class="text-gray-600">{{ selectedFirm.lawyersCount }} محامي</span>
                </div>
                <div class="flex items-center">
                  <Calendar class="w-5 h-5 text-gray-400 ml-2" />
                  <span class="text-gray-600">تأسس عام {{ selectedFirm.foundedYear }}</span>
                </div>
                <div class="flex items-center">
                  <Briefcase class="w-5 h-5 text-gray-400 ml-2" />
                  <span class="text-gray-600">{{ selectedFirm.casesHandled }} قضية معالجة</span>
                </div>
              </div>

              <div class="flex items-center">
                <div class="flex text-yellow-400">
                  <Star
                    v-for="i in selectedFirm.rating"
                    :key="i"
                    :class="i < selectedFirm.rating ? 'w-5 h-5 fill-current' : 'w-5 h-5'"
                  />
                </div>
                <span class="text-lg font-semibold text-gray-700 mr-2">{{ selectedFirm.rating }}/5.0</span>
              </div>
            </div>
          </div>
        </div>

        <div class="p-6">
          <!-- Action Buttons -->
          <div class="flex space-x-3 space-x-reverse mb-6">
            <button
              @click="contactFirm"
              class="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <MessageSquare class="w-5 h-5 ml-2" />
              استشارة
            </button>
            <button
              @click="bookAppointment"
              class="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <Calendar class="w-5 h-5 ml-2" />
              حجز موعد
            </button>
          </div>

          <!-- About Section -->
          <div class="mb-6">
            <h3 class="text-xl font-bold text-gray-800 mb-4">عن المكتب</h3>
            <p class="text-gray-700 leading-relaxed">{{ selectedFirm.description }}</p>
          </div>

          <!-- Team -->
          <div class="mb-6">
            <h3 class="text-lg font-semibold text-gray-800 mb-3">نبذة عن المكتب</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="lawyer in selectedFirm.team"
                :key="lawyer.id"
                class="flex items-center p-3 bg-gray-50 rounded-lg"
              >
                <img
                  :src="lawyer.avatar"
                  :alt="lawyer.name"
                  class="w-10 h-10 rounded-full object-cover ml-2"
                />
                <div>
                  <h4 class="font-medium text-gray-800">{{ lawyer.name }}</h4>
                  <p class="text-sm text-gray-600">{{ lawyer.position }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Contact Info -->
          <div class="p-4 bg-blue-50 rounded-lg">
            <h3 class="text-lg font-semibold text-gray-800 mb-3">معلومات الاتصال</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div class="flex items-center">
                <Phone class="w-4 h-4 text-blue-600 ml-2" />
                <span class="text-gray-700">{{ selectedFirm.phone }}</span>
              </div>
              <div class="flex items-center">
                <Mail class="w-4 h-4 text-blue-600 ml-2" />
                <span class="text-gray-700">{{ selectedFirm.email }}</span>
              </div>
              <div class="flex items-center">
                <Globe class="w-4 h-4 text-blue-600 ml-2" />
                <span class="text-gray-700">{{ selectedFirm.website }}</span>
              </div>
              <div class="flex items-center">
                <MapPin class="w-4 h-4 text-blue-600 ml-2" />
                <span class="text-gray-700">{{ selectedFirm.address }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Building, MapPin, Users, Calendar, Star, Phone, Mail, Globe, X, MessageSquare } from 'lucide-vue-next'

interface TeamMember {
  id: string
  name: string
  position: string
  avatar: string
}

interface Firm {
  id: string
  name: string
  logo: string
  specialization: string
  city: string
  lawyersCount: number
  foundedYear: number
  rating: number
  casesHandled: number
  description: string
  practiceAreas: string[]
  team: TeamMember[]
  phone: string
  email: string
  website: string
  address: string
}

const searchQuery = ref('')
const selectedSize = ref('')
const selectedCity = ref('')
const selectedSpecialization = ref('')
const selectedSpecializations = ref<string[]>([])
const selectedFirm = ref<Firm | null>(null)

const _sizes = [
  { id: 'small', name: 'صغير (1-5 محامين)' },
  { id: 'medium', name: 'متوسط (6-20 محامي)' },
  { id: 'large', name: 'كبير (21-50 محامي)' },
  { id: 'enterprise', name: 'مؤسسة (50+ محامي)' }
]

const specializations = [
  { id: 'corporate', name: 'قانون الشركات' },
  { id: 'commercial', name: 'القانون التجاري' },
  { id: 'civil', name: 'القانون المدني' },
  { id: 'criminal', name: 'القانون الجزائي' },
  { id: 'family', name: 'القانون الأسري' },
  { id: 'labor', name: 'قانون العمل' },
  { id: 'real-estate', name: 'قانون العقارات' },
  { id: 'intellectual-property', name: 'الملكية الفكرية' }
]

const cities = [
  { id: 'amman', name: 'عمان' },
  { id: 'irbid', name: 'إربد' },
  { id: 'zarqa', name: 'الزرقاء' },
  { id: 'aqaba', name: 'العقبة' },
  { id: 'salt', name: 'السلط' }
]

const firms: Firm[] = [
  {
    id: '1',
    name: 'مكتب الحداد للمحاماة والاستشارات القانونية',
    logo: 'https://images.unsplash.com/photo-1560472357-7c69319e1d0?w=150&h=150&fit=crop',
    specialization: 'القانون المدني والتجاري والجزائي',
    city: 'عمان',
    lawyersCount: 12,
    foundedYear: 2005,
    rating: 5,
    casesHandled: 1250,
    description: 'مكتب قانوني رائد في الأردن متخصص في تقديم الخدمات القانونية الشاملة للأفراد والشركات. يتميز بفريق من المحامين ذوي الخبرة العالية والالتزام بأعلى معايير المهنية.',
    practiceAreas: ['القضايا المدنية', 'القضايا التجارية', 'القضايا الجزائية', 'التحكيم', 'الاستشارات القانونية', 'صياغة العقود'],
    team: [
      {
        id: 't1',
        name: 'د. محمد الحداد',
        position: 'شريك مؤسس',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2?w=150&h=150&fit=crop&crop=face'
      },
      {
        id: 't2',
        name: 'أ. سمر الحداد',
        position: 'شريك مؤسس',
        avatar: 'https://images.unsplash.com/photo-1500648297693-5e0ed7e8b9f?w=150&h=150&fit=crop&crop=face'
      }
    ],
    phone: '+962 6 555 1234',
    email: 'info@alhadad-law.com',
    website: 'www.alhadad-law.com',
    address: 'شارع الملكة رقيا العبدالله، عمان، الأردن'
  },
  {
    id: '2',
    name: 'مجموعة المحامين العرب',
    logo: 'https://images.unsplash.com/photo-1451187580451-4dc9049555e5?w=150&h=150&fit=crop',
    specialization: 'القانون الدولي والتجارة',
    city: 'عمان',
    lawyersCount: 8,
    foundedYear: 2012,
    rating: 4,
    casesHandled: 680,
    description: 'مكتب متخصص في القانون الدولي والتجارة الدولية مع خبرة في التعامل مع القضايا العابرة للحدود.',
    practiceAreas: ['القانون الدولي', 'التجارة الدولية', 'الاستثمار الأجنبي', 'التحكيم الدولي', 'الملكية الفكرية'],
    team: [
      {
        id: 't3',
        name: 'ن. علي الخالدي',
        position: 'مدير المكتب',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5a8bb0c3e0f?w=150&h=150&fit=crop&crop=face'
      }
    ],
    phone: '+962 6 555 5678',
    email: 'info@arab-lawyers.com',
    website: 'www.arab-lawyers.com',
    address: 'مبنى الأعمال، دوار الشمس، عمان، الأردن'
  }
]

const filteredFirms = computed(() => {
  let filtered = firms

  if (selectedSize.value) {
    filtered = filtered.filter(firm => {
      if (selectedSize.value === 'small') return firm.lawyersCount <= 5
      if (selectedSize.value === 'medium') return firm.lawyersCount >= 6 && firm.lawyersCount <= 15
      if (selectedSize.value === 'large') return firm.lawyersCount >= 16
      return true
    })
  }

  if (selectedCity.value) {
    filtered = filtered.filter(firm => firm.city === selectedCity.value)
  }

  if (selectedSpecializations.value.length > 0) {
    filtered = filtered.filter(firm =>
      selectedSpecializations.value.some(spec =>
        firm.specialization.toLowerCase().includes(spec.toLowerCase())
      )
    )
  }

  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(firm =>
      firm.name.toLowerCase().includes(query) ||
      firm.specialization.toLowerCase().includes(query) ||
      firm.city.toLowerCase().includes(query)
    )
  }

  return filtered
})

const openFirmModal = (firm: Firm) => {
  selectedFirm.value = firm
}

const closeFirmModal = () => {
  selectedFirm.value = null
}

const contactFirm = () => {
  console.log('Contacting firm:', selectedFirm.value?.name)
  // TODO: Open contact modal or navigate to contact page
}

const _scheduleConsultation = () => {
  console.log('Scheduling consultation with:', selectedFirm.value?.name)
  // TODO: Navigate to consultation scheduling
}

const bookAppointment = () => {
  console.log('Booking appointment')
  // TODO: Implement appointment booking
}
</script>
